import { Link } from 'react-router-dom';
import { TrustBand, fmtScore, scoreColor } from './trustShared';
import TrustStance from './TrustStance';
import { recombine } from './lensWeights';
import { Explain } from './Explain';
import Byline from './Byline';
import { snip as snipAt } from './snip';

// The row of the merged front door's stream — one row shape for every pill
// except the reading queue, whose rows are DOCUMENTS and a different grain.
//
// Lifted out of Home.js on 2026-08-07 when /resolve and Browse folded into the
// pill row (HANDOFF 0c). It was already the richest row in the app — named
// tension, band, blend, one-click stake — and it was reachable from exactly one
// page. The viewset surfaces rendered content and a date while fetching the
// very same scores and throwing the tension away. So the fold is an UPGRADE to
// the viewset views rather than a downgrade of /resolve, which is the only
// reason it is safe to have one row instead of two.
//
// ── THE NO-SIGNAL CASE, which is new here ───────────────────────────────────
// /resolve never had to render it: it dropped no-signal objects before they
// reached a row. Two of the folded ranking rules — `needs judgment` and `new` —
// are ABOUT those objects, so the row now meets them, and it must render
// ABSENCE rather than a band sitting at zero. A zero band reads as "the lenses
// looked and found perfect balance"; the truth is that no lens can see the
// object at all. Same rule the question object follows on ObjectDetail.

const snip = (s, n = 180) => snipAt(s, n);

function StreamRow({ item, weights, onStanceChanged }) {
  const tension = item.tension || null;
  const noSignal = !tension || tension.state === 'no_signal';
  const contested = tension?.state === 'contested';
  const headline = contested && tension.top ? tension.top.sentence : null;
  const blend = noSignal ? null : recombine(item.scores || {}, weights);

  return (
    <div className="trust-row" style={{ cursor: 'default', alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {headline && (
          <div style={{ fontSize: 'var(--wv-fs-ui)', fontWeight: 600, marginBottom: 'var(--wv-sp-1)' }}>
            <span style={{ color: 'var(--wv-warn)' }}>⚡</span>{' '}
            <Explain concept="tension">{headline}</Explain>
          </div>
        )}
        <div style={{ fontSize: headline ? 'var(--wv-fs-md)' : 'var(--wv-fs-ui)', fontWeight: headline ? 'normal' : 600, lineHeight: 'var(--wv-lh)' }}>
          <Link to={`/object/${item.id}`} style={{ color: 'inherit' }}>
            {snip(item.content)}
          </Link>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--wv-sp-5)', marginTop: 'var(--wv-sp-3)', flexWrap: 'wrap' }}>
          {noSignal ? (
            // Absence, never a zero — and it is a WORK-LIST line, so it says
            // what would change it rather than only what is missing.
            <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
              <Explain concept="no-signal">no trust signal yet</Explain> — a vote on a
              connection is what lights it up
            </span>
          ) : (
            <>
              <TrustBand scores={item.scores} blend={blend} width={150} height={12} />
              <span className="trust-muted wv-data" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                blend <strong style={{ color: scoreColor(blend) }}>{fmtScore(blend)}</strong>
              </span>
              {!contested && (
                <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>consensus</span>
              )}
              {item.scores?.adversarial_consensus === 0 && (
                <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-xs)' }}>
                  hasn’t survived cross-group division yet
                </span>
              )}
            </>
          )}
          {item.object_type === 'text' && (
            <Link to={`/read/${item.id}`} style={{ fontSize: 'var(--wv-fs-xs)' }}>read</Link>
          )}
          {/* WHO SAID IT (2026-08-14). This was `by {creator_username}`, which
              is the ACCOUNT THAT ENTERED the object and not its author — on a
              claim decomposed from a book it put the importer's name under
              somebody else's assertion. The shared byline says who is on
              record, or "unattributed" when nobody is.

              `creator_username` has not moved elsewhere and is not lost: it is
              graph machinery, and the object page already carries it in the L2
              machinery fold where it reads as what it is. */}
          <Byline
            attribution={item.attribution}
            slots={['authored_by', 'asserted_by']}
            style={{ marginTop: 0 }}
          />

          {/* one-click stake on the judging surface (vector-trust tail):
              my_trust is BATCHED into the stream payload — controlled mode,
              no per-row fetch */}
          <TrustStance
            targetId={item.id}
            kind="claim"
            compact
            preloaded={item.my_trust ? { id: item.my_trust.edge_id, polarity: item.my_trust.polarity, my_vote: null } : null}
            onChanged={onStanceChanged}
          />
        </div>
      </div>
    </div>
  );
}

export default StreamRow;
